import {EAnnotLevel, ISkAnnot, ISkTextAnnot, SkAnnotBase} from "lib/edit/schema/schemaAnnots";
import {SchemaDom, SkNode} from "lib/edit/schema/schema";
import {DOM} from "lib/commons/xml/dom";
import {XA} from "lib/commons/xml/xAddr";
import {CommentsState} from "lib/edit/ot/xmlTypedHouse";
import IS_comment = DOM.IS_comment;
import IS_text = DOM.IS_text;

/**
 * Module de recherche associé à un SchemaDom
 */
export interface ISchemaSearch {

	buildSearchAnnots(skNode: SkNode, annots: ISkAnnot[]): void

	maxResults: number

	/**
	 * Si la méthode existe, appelé une fois avant une mise à jour de tout ou partie des annotations
	 * de recherche associées à chaque noeud via buildSearchAnnots(skNode, annots)
	 */
	beforeSearchAnnots?(schemaDom: SchemaDom): void
}

export interface ISkSearchAnnot extends ISkAnnot {
	search: ISchemaSearch
}

export function isSkSearchAnnot(a: ISkAnnot): a is ISkSearchAnnot {return a && (a.type === SkSearchTextAnnot.TYPE)}

/** Annotation de search sur un fragment de texte. */
export class SkSearchTextAnnot extends SkAnnotBase implements ISkSearchAnnot, ISkTextAnnot {
	static readonly TYPE = "search";

	get type() {return SkSearchTextAnnot.TYPE};

	get level() {return EAnnotLevel.search};

	get start() {return XA.append(XA.fromNode(this.anchorNode), this.offsetStart)}

	get len() {return this.match.length}

	/**
	 * @param keyEquals clé évaluée dans equals() permettant de juger 2 annots différentes par exemple si le noeud texte contenant a changé.
	 * Force ainsi le redessinement de toutes les annots de search d'un noeud texte modifié.
	 */
	constructor(public search: ISchemaSearch, anchorNode: Node, public offsetStart: number, public match: string, protected keyEquals: string) {
		super();
		this.anchorNode = anchorNode;
	}

	getLabel(): string {return this.match}

	equals(other: SkSearchTextAnnot | any) {return this.anchorNode === other.anchorNode && this.search === other.search && this.offsetStart === other.offsetStart && this.keyEquals === other.keyEquals}

	toJSON(): any {return null} //pas sérialisable

}


/** Annotation de search sur un un elt ou un attribut. */
export class SkSearchNodeAnnot extends SkAnnotBase implements ISkSearchAnnot {

	get type() {return SkSearchTextAnnot.TYPE};

	get level() {return EAnnotLevel.search};

	constructor(public search: ISchemaSearch, anchorNode: Node, protected label: string) {
		super();
		this.anchorNode = anchorNode;
	}

	getLabel(): string {return this.label}

	equals(other: SkSearchTextAnnot | any) {return this.anchorNode === other.anchorNode && this.search === other.search}

	toJSON(): any {return null} //pas sérialisable
}

/** Annotation de search sur un comment node qui ref une subAnnot dans le CommentHouse. */
export class SkSearchCommentAnnot extends SkSearchNodeAnnot implements ISkSearchAnnot {

	constructor(search: ISchemaSearch, anchorNode: Node, public subAnnot: ISkSearchAnnot) {
		super(search, anchorNode, null);
	}

	getLabel(): string {return this.subAnnot.getLabel()}

	equals(other: SkSearchCommentAnnot | any) {return this.anchorNode === other.anchorNode && this.search === other.search && this.subAnnot.equals(other.subAnnot)}
}

/**
 * Impl recherche dans les noeuds textes et les commentaires (pas les attributs)
 */
export class SkTextSearch implements ISchemaSearch {

	constructor(readonly pattern: RegExp, public maxResults: number) {
		if (!pattern.global) throw Error("Pattern must be global");
	}

	buildSearchAnnots(skNode: SkNode, annots: ISkAnnot[]): void {
		//console.log("buildSearchAnnots:::", skNode);
		const meta = skNode.rule.skMeta;
		const isSeacrhInText = meta?.isTextSearchable(skNode, skNode.node);
		for (let ch = skNode.node.firstChild; ch; ch = ch.nextSibling) {
			if (isSeacrhInText && IS_text(ch)) {
				let r = this.pattern.exec(ch.nodeValue);
				while (r) {
					annots.push(new SkSearchTextAnnot(this, ch, r.index, r[0], ch.nodeValue));
					r = this.pattern.exec(ch.nodeValue);
				}
			} else if (IS_comment(ch)) {
				const house = skNode.schemaDom.house;
				if (house) {
					const cmtMgr = CommentsState.getCmtHouseFromNode(house, ch, skNode);
					if (cmtMgr.house) {
						const filter = {
							search: this,
							schemaDom: skNode.schemaDom,
							annots: annots,
							cmt: ch,
							acceptNode(node: Node): number {
								const skN = this.schemaDom.getSkNode(node);
								if (skN) {
									const l = this.annots.length;
									this.search.buildSearchAnnots(skN, this.annots);
									for (let i = l; i < this.annots.length; i++) {
										this.annots[i] = new SkSearchCommentAnnot(this.search, this.cmt, this.annots[i] as ISkSearchAnnot);
									}
								} else {
									console.trace("No skNode for", node);
								}
								return NodeFilter.FILTER_ACCEPT;
							}
						};
						const tw = cmtMgr.house.document.createTreeWalker(cmtMgr.house.root, NodeFilter.SHOW_ELEMENT, filter);
						while (tw.nextNode() && annots.length <= this.maxResults) ;
					} else if (cmtMgr.futureHouse) {
						console.trace("TODO async CommentHouse fetching");
						//TODO revalidation complete du skNode lorsque la house est dispo
					} else {
						//texte brut
						let r = this.pattern.exec(ch.nodeValue);
						while (r) {
							annots.push(new SkSearchTextAnnot(this, ch, r.index, r[0], ch.nodeValue));
							r = this.pattern.exec(ch.nodeValue);
						}
					}
				}
			}
		}
	}
}

/** Recherche dans les noeuds textes ET dans les attributs. */
export class SkTextSearchInAtts extends SkTextSearch {

	buildSearchAnnots(skNode: SkNode, annots: ISkAnnot[]): void {
		const elt = skNode.node;
		if (elt instanceof Element) for (let i = 0; i < elt.attributes.length; i++) {
			const att = elt.attributes.item(i);
			const attRule = skNode.rule.contentRule.findRuleAttrFor(att.nodeName)
			if (attRule?.skMeta.isTextSearchable(skNode, att)) {
				let r = this.pattern.exec(att.nodeValue);
				while (r) {
					annots.push(new SkSearchTextAnnot(this, att, r.index, r[0], att.nodeValue));
					r = this.pattern.exec(att.nodeValue);
				}
			}
		}
		super.buildSearchAnnots(skNode, annots);
	}
}

/**
 * Impl recherche par xpath
 */
export class SkXPathSearch implements ISchemaSearch {

	lastSearch = new Set<Node>()

	constructor(readonly xpath: XPathExpression, public label: string, public maxResults: number) {
	}

	beforeSearchAnnots(schemaDom: SchemaDom) {
		this.lastSearch.clear()
		const r = this.xpath.evaluate(schemaDom.nodeRoot, XPathResult.UNORDERED_NODE_ITERATOR_TYPE);
		for (let n = r.iterateNext(); n !== null; n = r.iterateNext()) this.lastSearch.add(n);
	}

	buildSearchAnnots(skNode: SkNode, annots: ISkAnnot[]): void {
		if (this.lastSearch.has(skNode.node)) {
			annots.push(new SkSearchNodeAnnot(this, skNode.node, this.label));
		}
	}
}

/**
 * Impl recherche par xpath
 */
export class SkCssSelectorSearch implements ISchemaSearch {

	constructor(readonly selector: string, public label: string, public maxResults: number) {
	}

	buildSearchAnnots(skNode: SkNode, annots: ISkAnnot[]): void {
		if (skNode.node instanceof Element && skNode.node.matches(this.selector)) {
			annots.push(new SkSearchNodeAnnot(this, skNode.node, this.label));
		}
	}
}